import {
  AHB_MESSAGE_TYPE,
  AHB_PROCEDURE_ID,
  AHB_PROCESSING_CODE,
  AHB_REHAB_TYPE,
  AHB_SUBPROCEDURE,
  GUARDIAN,
  REHAB_ADMISSION,
  REHAB_DIRECTIVE_TYPE,
  REHAB_FOCUS,
  SCHEMA_VERSION,
  TRANSPORTATION_CODE,
} from "core/consts";
import type { SchemaVersion } from "core/types";
import { REHAB_FOCUS_VALUE_TO_CODE } from "onprem/mapping/mapFormToRehabRequest";
import { z } from "zod";
import {
  ahbDateinummerSchema,
  AHBdateSchema,
  AHBdateSchemaV2,
  AHBfloorSchema,
  AHBpostleitzahlSchema,
  AHBtimeSchema,
  ICDCodeSchema,
  ICDReasonSchema,
  IKNumberSchema,
} from "./shared";

export const AHBRequestPayloadSchema = z.object({
  Nutzdaten: z.object({
    "@xmlns": z.string(),
    "@xmlns:aar": z.string(),
    "@xmlns:arh": z.string(),
    "@xmlns:feh": z.string(),
    "@xmlns:gdb": z.string(),
    "@xmlns:sto": z.string(),
    Body: z.object({
      Anschlussrehabilitation: z.object({
        "aar:Antrag": z.object({
          "aar:Antrag": z.object({
            "arh:Alltagsrelevante_Beeintraechtigungen": z.object({
              "arh:Beeintraechtigung": z
                .array(
                  z.object({
                    "arh:Item": z.string().length(2),
                    "arh:Punkte": z.number().int().min(0).max(5),
                  }),
                )
                .max(20),
              "arh:Gesamtpunkte": z.number().int().min(0).max(100),
            }),
            "arh:Angaben_des_Krankenhauses": z.object({
              "arh:Ansprechpartner": z.object({
                "arh:Fax": z.string().max(25).optional(),
                "arh:Nachname": z.string().max(45),
                "arh:Telefon": z.string().max(25),
                "arh:Vorname": z.string().max(45).optional(),
              }),
              "arh:Kommunikation": z.object({
                "arh:Deutsche_Sprache": z.enum(["0", "1"]),
                "arh:Sprache": z.string().max(150).optional(),
              }),
              "arh:Krankenhausbehandlung": z.object({
                "arh:Beginn": AHBdateSchema,
                "arh:Ende": AHBdateSchema,
                "arh:Version_ICD_OPS": z.string().length(4),
              }),
              "arh:Rehaeinrichtung": z
                .object({
                  "arh:Angefragte_Rehaeinrichtung": z
                    .object({
                      "arh:Anschrift_Rehaeinrichtung": z
                        .object({
                          "arh:Hausnummer": z.string().max(9),
                          "arh:Name": z.string().max(45),
                          "arh:Ort": z.string().max(40),
                          "arh:Postleitzahl": z.string().max(10),
                          "arh:Strasse": z.string().max(46),
                          "arh:Telefon": z.string().max(25),
                        })
                        .optional(),
                      "arh:Institutionskennzeichen": z
                        .string()
                        .max(9)
                        .optional(),
                    })
                    .optional(),
                  "arh:Fruehestmoegliche_Aufnahme": AHBdateSchema.optional(),
                  "arh:Rehaeinrichtung_nicht_angefragt": z
                    .literal(1)
                    .optional(),
                })
                .superRefine((val, ctx) => {
                  const hasNichtAngefragt =
                    val["arh:Rehaeinrichtung_nicht_angefragt"] === 1;
                  const angefragt = val["arh:Angefragte_Rehaeinrichtung"];
                  const aufnahme = val["arh:Fruehestmoegliche_Aufnahme"];

                  if (hasNichtAngefragt && angefragt) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message:
                        "Either 'Rehaeinrichtung_nicht_angefragt' or 'Angefragte_Rehaeinrichtung' may be set — not both.",
                      path: ["arh:Rehaeinrichtung_nicht_angefragt"],
                    });
                  }

                  if (!hasNichtAngefragt && !angefragt) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message:
                        "Either 'Rehaeinrichtung_nicht_angefragt' or 'Angefragte_Rehaeinrichtung' must be set.",
                      path: ["arh:Angefragte_Rehaeinrichtung"],
                    });
                  }

                  if (angefragt) {
                    const hasIK = !!angefragt["arh:Institutionskennzeichen"];
                    const hasAnschrift =
                      !!angefragt["arh:Anschrift_Rehaeinrichtung"];

                    if (!hasIK && !hasAnschrift) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message:
                          "'Angefragte_Rehaeinrichtung' must contain either 'Institutionskennzeichen' or 'Anschrift_Rehaeinrichtung'.",
                        path: ["arh:Angefragte_Rehaeinrichtung"],
                      });
                    }

                    if (hasIK && hasAnschrift) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message:
                          "'Angefragte_Rehaeinrichtung' must not contain both 'Institutionskennzeichen' and 'Anschrift_Rehaeinrichtung'.",
                        path: ["arh:Angefragte_Rehaeinrichtung"],
                      });
                    }

                    if (!aufnahme) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message:
                          "'Fruehestmoegliche_Aufnahme' is required when a rehab facility has been contacted.",
                        path: ["arh:Fruehestmoegliche_Aufnahme"],
                      });
                    }
                  }
                }),
              "arh:Standortnummer": z.string().min(1).max(9),
            }),
            "arh:Angaben_des_Versicherten": z.object({
              "arh:Anforderungen_Wuensche": z.string().max(600).optional(),
              "arh:Antrag_Pflegebeduerftigkeit": z.enum(["0", "1"]),
              "arh:Betreuung_Vollmacht_Vertreter": z
                .array(
                  z.object({
                    "arh:Hausnummer": z.string().max(9).optional(),
                    "arh:Kennzeichen_Vertretung": z.nativeEnum(GUARDIAN),
                    "arh:Nachname": z.string().max(45),
                    "arh:Ort": z.string().max(40).optional(),
                    "arh:Postleitzahl": AHBpostleitzahlSchema().optional(),
                    "arh:Strasse": z.string().max(46).optional(),
                    "arh:Telefon": z.string().max(25).optional(),
                    "arh:Vorname": z.string().max(45),
                  }),
                )
                .max(2)
                .optional(),
              "arh:Haeusliche_Situation": z.object({
                "arh:Aufzug": z.enum(["0", "1"]),
                "arh:Besonderheiten": z.string().max(600).optional(),
                "arh:Stockwerk": AHBfloorSchema(),
              }),
              "arh:Haeusliche_Versorgungssituation": z.object({
                "arh:Versorgung_Sichergestellt": z.enum(["0", "1"]),
                "arh:Wohnsituation": z.enum(["1", "2", "3", "4"]),
              }),
              "arh:Hausarzt": z
                .object({
                  "arh:Hausnummer": z.string().max(9).optional(),
                  "arh:Nachname": z.string().max(45),
                  "arh:Ort": z.string().max(40),
                  "arh:Postleitzahl": AHBpostleitzahlSchema().optional(),
                  "arh:Strasse": z.string().max(46).optional(),
                  "arh:Telefon": z.string().max(25).optional(),
                  "arh:Vorname": z.string().max(45),
                })
                .optional(),
              "arh:Rentenantragstellung": z.object({
                "arh:Altersrente": z.enum(["0", "1"]),
                "arh:Erwerbsminderungsrente": z.enum(["0", "1"]),
                "arh:Traeger_der_Altersrente": z.string().max(150).optional(),
                "arh:Traeger_der_Erwerbsminderungsrente": z
                  .string()
                  .max(150)
                  .optional(),
              }),
            }),
            "arh:Behandlungen": z.object({
              "arh:Andere_Behandlung": z.string().max(150).optional(),
              "arh:Operation_durchgefuehrt": z.enum(["0", "1"]),
              "arh:Operationen": z
                .object({
                  "arh:Operation": z
                    .array(
                      z.object({
                        "arh:OP_Datum": AHBdateSchema,
                        "arh:OPS_Codes": z.object({
                          "arh:OPS": z.array(z.string().max(8)),
                        }),
                        "arh:Wundheilung": z.enum(["0", "1"]),
                      }),
                    )
                    .min(1)
                    .max(5),
                })
                .nullable()
                .optional(),
            }),
            "arh:Einwilligung_Uebermittlung": z.enum(["0", "1"]),
            "arh:Entbindung_Schweigepflicht": z.enum(["0", "1"]),
            "arh:Fruehrehabilitation": z.enum(["0", "1"]),
            "arh:Funktionsdiagnosen": z.object({
              "arh:Funktionsdiagnose": z
                .array(
                  z.object({
                    "arh:Diagnose": z.string().max(150),
                    "arh:ICD_10_GM": ICDCodeSchema,
                    "arh:Lfd_Nr": z.enum(["1", "2", "3", "4", "5"]),
                    "arh:Ursache": ICDReasonSchema,
                  }),
                )
                .min(1)
                .max(5),
            }),
            "arh:Komplikationen": z.object({
              "arh:Art_der_Komplikation": z
                .object({
                  "arh:Kardiovaskulaere_Komplikation": z
                    .string()
                    .max(150)
                    .optional(),
                  "arh:Komplikation_mit_AR_Diagnose": z
                    .string()
                    .max(150)
                    .optional(),
                  "arh:Sonstige_Komplikation": z.string().max(150).optional(),
                })
                .partial()
                .optional(),
              "arh:Keime": z
                .object({
                  "arh:Art_der_Keime": z.string().max(150).optional(),
                  "arh:Keimbesiedelung": z.enum(["0", "1", "2"]),
                })
                .optional(),
              "arh:Komplikationen_aufgetreten": z.enum(["0", "1"]),
            }),
            "arh:Rehabilitationsfaehigkeit": z
              .object({
                "arh:Anpassung_Interimsprothese_erfolgt": z.enum(["0", "1"]),
                "arh:Ausreichende_Therapiebelastbarkeit": z.enum(["0", "1"]),
                "arh:Belastbarkeit_nach_OP": z.enum(["1", "2", "3"]).optional(),
                "arh:Besonderheiten": z.string().max(150).optional(),
                "arh:Kriterien_FR_Index": z
                  .object({
                    "arh:Kriterium": z
                      .array(z.number().int().max(7))
                      .min(1)
                      .max(7),
                  })
                  .optional(),
                "arh:Teilbelastbar_mit": z.number().int().max(999).optional(),
              })
              .superRefine((data, ctx) => {
                // If Belastbarkeit_nach_OP is "2", Teilbelastbar_mit must be provided
                if (data["arh:Belastbarkeit_nach_OP"] === "2") {
                  if (data["arh:Teilbelastbar_mit"] === undefined) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message:
                        '"Teilbelastbar_mit" is required when Belastbarkeit_nach_OP is "2" (teilweise belastbar)',
                      path: ["arh:Teilbelastbar_mit"],
                    });
                  }
                }
              }),
            "arh:Rehabilitationsziel": z.string().max(600),
            "arh:Verarbeitungskennzeichen": z.literal(
              AHB_PROCESSING_CODE.INITIAL_REQUEST,
            ),
            "arh:Verlegung": z
              .object({
                "arh:Begruendung": z.string().max(600).optional(),
                "arh:Datum_Verlegung": AHBdateSchema.optional(),
                "arh:Kennzeichen_Verlegung": z.nativeEnum(REHAB_ADMISSION),
              })
              .superRefine((val, ctx) => {
                const kennzeichen = val["arh:Kennzeichen_Verlegung"];

                if (
                  (kennzeichen === REHAB_ADMISSION.WITHIN_TWO_WEEKS ||
                    kennzeichen === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS) &&
                  !val["arh:Datum_Verlegung"]
                ) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                      "Datum_Verlegung is required when Kennzeichen_Verlegung is '2' or '3'.",
                    path: ["arh:Datum_Verlegung"],
                  });
                }

                if (
                  kennzeichen === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS &&
                  !val["arh:Begruendung"]
                ) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                      "Begruendung is required when Kennzeichen_Verlegung is '3'.",
                    path: ["arh:Begruendung"],
                  });
                }
              }),
            "arh:Weitere_Informationen": z.object({
              "arh:Anreise": z.object({
                "arh:Begleitperson": z.enum(["0", "1"]),
                "arh:Begruendung_KTW": z.string().max(150).optional(),
                "arh:Verkehrsmittel": z.nativeEnum(TRANSPORTATION_CODE),
              }),
              "arh:Besondere_Anforderungen": z.object({
                "arh:Anforderungen": z
                  .object({
                    "arh:Anforderung": z
                      .array(
                        z.string().regex(/^(0[1-9]|1[0-7])$/, {
                          message: "Must be a number between 01 and 17",
                        }),
                      )
                      .min(1)
                      .max(17),
                    "arh:Dialyseverfahren": z.string().max(150).optional(),
                    "arh:Erlaeuterung": z.string().max(600).optional(),
                    "arh:Grad_Dekubitus": z
                      .enum(["1", "2", "3", "4"])
                      .optional(),
                    "arh:Last_bis": z.number().int().max(999).optional(),
                    "arh:Pflegegrad_Angehoeriger": z
                      .enum(["1", "2", "3", "4", "5"])
                      .optional(),
                  })
                  .optional(),
                "arh:Keine_besonderen_Anforderungen": z.literal("1").optional(),
              }),
              "arh:Krankenhausarzt": z.object({
                "arh:Datum": AHBdateSchema,
                "arh:Nachname": z.string().max(45),
                "arh:Telefon": z.string().max(25),
                "arh:Titel": z.string().max(20).optional(),
                "arh:Vorname": z.string().max(45),
              }),
            }),
            "arh:Willenserklaerung_Unterschrift": z.literal(1),
            "arh:Zusammenfassende_Bewertung": z
              .object({
                "arh:Begruendung_Rehabilitationsart": z
                  .string()
                  .max(150)
                  .optional(),
                "arh:Begruendungen": z
                  .object({
                    "arh:Begruendung": z
                      .array(z.nativeEnum(REHAB_DIRECTIVE_TYPE))
                      .min(1)
                      .max(12),
                  })
                  .optional(),
                "arh:Empfohlene_Rehabilitationsart": z.union([
                  z.literal(AHB_REHAB_TYPE.STATIONARY),
                  z.literal(AHB_REHAB_TYPE.AMBULANT),
                  z.literal(AHB_REHAB_TYPE.AMBULANT_MOBIL),
                ]),
                "arh:Fall_Paragraph_16": z.enum(["0", "1"]),
                "arh:Schwerpunkt": z
                  .object({
                    "arh:Erlaeuterung": z.string().max(600).optional(),
                    "arh:Rehaschwerpunkt": z.string().regex(/^\d{2}$/),
                  })
                  .refine(
                    (data) =>
                      data["arh:Rehaschwerpunkt"] !==
                        REHAB_FOCUS_VALUE_TO_CODE[REHAB_FOCUS.OTHER] ||
                      !!data["arh:Erlaeuterung"],
                    {
                      message: `"arh:Erlaeuterung" is required when "arh:Rehaschwerpunkt" is "${String(
                        REHAB_FOCUS.OTHER,
                      )}"`,
                      path: ["arh:Erlaeuterung"],
                    },
                  ),
              })
              .refine(
                (data) =>
                  data["arh:Fall_Paragraph_16"] !== "1" ||
                  (data["arh:Begruendungen"] &&
                    data["arh:Begruendungen"]["arh:Begruendung"].length > 0),
                {
                  message: `"arh:Begruendungen" is required when "arh:Fall_Paragraph_16" is "1"`,
                  path: ["arh:Begruendungen"],
                },
              ),
          }),
          "aar:Versicherter": z.object({
            "gdb:Anschriftenzusatz": z.string().max(40).optional(),
            "gdb:Geburtsdatum": AHBdateSchema,
            "gdb:Geschlecht": z.enum(["w", "m", "d", "x"]),
            "gdb:Hausnummer": z.string().max(9).optional(),
            "gdb:KH_internes_Kennzeichen": z.string().max(15),
            "gdb:Krankenversichertennummer": z.string().min(10).max(12),
            "gdb:Laenderkennzeichen": z.string().max(3).optional(),
            "gdb:Nachname": z.string().max(45),
            "gdb:Namenszusatz": z.string().max(20).optional(),
            "gdb:Ort": z.string().max(40).optional(),
            "gdb:Postleitzahl": AHBpostleitzahlSchema().optional(),
            "gdb:Staatsangehoerigkeit": z.string().optional(),
            "gdb:Strasse": z.string().max(46).optional(),
            "gdb:Telefon": z.string().max(25).optional(),
            "gdb:Titel": z.string().max(20).optional(),
            "gdb:Vorname": z.string().max(45),
            "gdb:Vorsatzwort": z.string().max(20).optional(),
          }),
        }),
      }),
    }),
    Header: z.object({
      "gdb:Absender": z.object({
        "gdb:Krankenhaus": z.object({
          "gdb:Institutionskennzeichen": IKNumberSchema,
        }),
      }),
      "gdb:Datei_ID": z.uuid(),
      "gdb:Empfaenger": z.object({
        "gdb:Krankenkasse": z.object({
          "gdb:Institutionskennzeichen": IKNumberSchema,
        }),
      }),
      "gdb:Erstellungsdatum": AHBdateSchema,
      "gdb:Erstellungszeit": AHBtimeSchema,
      "gdb:Logische_Version": z.literal(SCHEMA_VERSION.V1_1_0),
      "gdb:Nachrichtentyp": z.literal(AHB_MESSAGE_TYPE.REQUEST),
      "gdb:Unterverfahren": z.literal(AHB_SUBPROCEDURE),
      "gdb:Verfahrenskennung": z.nativeEnum(AHB_PROCEDURE_ID),
      "gdb:Dateinummer": ahbDateinummerSchema,
    }),
  }),
});

const nutzdatenShape = AHBRequestPayloadSchema.shape.Nutzdaten.shape;
const headerShape = nutzdatenShape.Header.shape;
const {
  "gdb:Logische_Version": _logischeVersion,
  ...headerShapeWithoutVersion
} = headerShape;

const antragOuterShape =
  nutzdatenShape.Body.shape.Anschlussrehabilitation.shape["aar:Antrag"].shape;
const antragInnerShape = antragOuterShape["aar:Antrag"].shape;

const {
  "arh:Einwilligung_Uebermittlung": _einwilligungUebermittlung,
  ...antragInnerShapeV2
} = antragInnerShape;

// --- V2.0: Zusammenfassende_Bewertung modifications ---
// Feldgruppe "Begründungen" removed, "Begründung" changed to 0-1
// (Technische Anlage 2.0, §4.4)
const zusammenfassendeBewertungV2 = z.object({
  "arh:Begruendung_Rehabilitationsart": z.string().max(150).optional(),
  "arh:Begruendung": z.nativeEnum(REHAB_DIRECTIVE_TYPE).optional(),
  "arh:Empfohlene_Rehabilitationsart": z.union([
    z.literal(AHB_REHAB_TYPE.STATIONARY),
    z.literal(AHB_REHAB_TYPE.AMBULANT),
    z.literal(AHB_REHAB_TYPE.AMBULANT_MOBIL),
  ]),
  "arh:Fall_Paragraph_16": z.enum(["0", "1"]),
  "arh:Schwerpunkt": z
    .object({
      "arh:Erlaeuterung": z.string().max(600).optional(),
      "arh:Rehaschwerpunkt": z.string().regex(/^\d{2}$/),
    })
    .refine(
      (data) =>
        data["arh:Rehaschwerpunkt"] !==
          REHAB_FOCUS_VALUE_TO_CODE[REHAB_FOCUS.OTHER] ||
        !!data["arh:Erlaeuterung"],
      {
        message: `"arh:Erlaeuterung" is required when "arh:Rehaschwerpunkt" is "${String(
          REHAB_FOCUS.OTHER,
        )}"`,
        path: ["arh:Erlaeuterung"],
      },
    ),
});

// --- V2.0: Date format changed from YYYYMMDD to YYYY-MM-DD ---
// Override all nested shapes containing date fields for v2.0

const versicherterV2 = z.object({
  ...antragOuterShape["aar:Versicherter"].shape,
  "gdb:Geburtsdatum": AHBdateSchemaV2,
});

const angabenDesKrankenhausesShapeV1 =
  antragInnerShape["arh:Angaben_des_Krankenhauses"].shape;

const {
  "arh:Krankenhausbehandlung": _krankenhausbehandlungV1,
  ...angabenShapeV1WithoutKH
} = angabenDesKrankenhausesShapeV1;

const krankenhausbehandlungV2 = z.object({
  "arh:Beginn": AHBdateSchemaV2,
  "arh:Ende": AHBdateSchemaV2,
  "arh:Version_ICD_OPS": z.string().length(4),
});

const angabenDesKrankenhausesV2 = z.object({
  ...angabenShapeV1WithoutKH,
  "arh:Name_Krankenhaus": z.string().max(45),
  "arh:Strasse": z.string().max(46),
  "arh:Hausnummer": z.string().max(9).optional(),
  "arh:Postleitzahl": z.string().max(10),
  "arh:Ort": z.string().max(40),
  // v2.0: New wrapper arh:Angefragte_Rehaeinrichtungen with repeatable facilities (§4.4)
  "arh:Rehaeinrichtung": z
    .object({
      "arh:Angefragte_Rehaeinrichtungen": z
        .object({
          "arh:Angefragte_Rehaeinrichtung": z
            .array(
              z
                .object({
                  "arh:Anschrift_Rehaeinrichtung": z
                    .object({
                      "arh:Hausnummer": z.string().max(9),
                      "arh:Name": z.string().max(45),
                      "arh:Ort": z.string().max(40),
                      "arh:Postleitzahl": z.string().max(10),
                      "arh:Strasse": z.string().max(46),
                      "arh:Telefon": z.string().max(25),
                    })
                    .optional(),
                  "arh:Aufnahme_moeglich": z.number().int().min(0).max(2),
                  "arh:Fruehestmoegliche_Aufnahme": AHBdateSchemaV2.optional(),
                  "arh:Institutionskennzeichen": z.string().max(9).optional(),
                })
                .superRefine((val, ctx) => {
                  const hasIK = !!val["arh:Institutionskennzeichen"];
                  const hasAnschrift = !!val["arh:Anschrift_Rehaeinrichtung"];
                  if (!hasIK && !hasAnschrift) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message:
                        "'Angefragte_Rehaeinrichtung' must contain either 'Institutionskennzeichen' or 'Anschrift_Rehaeinrichtung'.",
                      path: ["arh:Angefragte_Rehaeinrichtung"],
                    });
                  }
                  if (hasIK && hasAnschrift) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message:
                        "'Angefragte_Rehaeinrichtung' must not contain both 'Institutionskennzeichen' and 'Anschrift_Rehaeinrichtung'.",
                      path: ["arh:Angefragte_Rehaeinrichtung"],
                    });
                  }
                }),
            )
            .min(1)
            .max(99),
        })
        .optional(),
      "arh:Rehaeinrichtung_nicht_angefragt": z.literal(1).optional(),
    })
    .superRefine((val, ctx) => {
      const hasNichtAngefragt =
        val["arh:Rehaeinrichtung_nicht_angefragt"] === 1;
      const hasAngefragt = !!val["arh:Angefragte_Rehaeinrichtungen"];

      if (hasNichtAngefragt && hasAngefragt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Either 'Rehaeinrichtung_nicht_angefragt' or 'Angefragte_Rehaeinrichtung' may be set — not both.",
          path: ["arh:Rehaeinrichtung_nicht_angefragt"],
        });
      }
      if (!hasNichtAngefragt && !hasAngefragt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Either 'Rehaeinrichtung_nicht_angefragt' or 'Angefragte_Rehaeinrichtung' must be set.",
          path: ["arh:Angefragte_Rehaeinrichtung"],
        });
      }
    }),
});

const behandlungenV2 = z.object({
  "arh:Andere_Behandlung": z.string().max(150).optional(),
  "arh:Operation_durchgefuehrt": z.enum(["0", "1"]),
  "arh:Operationen": z
    .object({
      "arh:Operation": z
        .array(
          z.object({
            "arh:OP_Datum": AHBdateSchemaV2,
            "arh:OPS_Codes": z.object({
              "arh:OPS": z.array(z.string().max(8)),
            }),
            "arh:Wundheilung": z.enum(["0", "1"]),
          }),
        )
        .min(1)
        .max(5),
    })
    .nullable()
    .optional(),
});

const verlegungV2 = z
  .object({
    "arh:Begruendung": z.string().max(600).optional(),
    "arh:Datum_Verlegung": AHBdateSchemaV2.optional(),
    "arh:Kennzeichen_Verlegung": z.nativeEnum(REHAB_ADMISSION),
  })
  .superRefine((val, ctx) => {
    const kennzeichen = val["arh:Kennzeichen_Verlegung"];
    if (
      (kennzeichen === REHAB_ADMISSION.WITHIN_TWO_WEEKS ||
        kennzeichen === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS) &&
      !val["arh:Datum_Verlegung"]
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Datum_Verlegung is required when Kennzeichen_Verlegung is '2' or '3'.",
        path: ["arh:Datum_Verlegung"],
      });
    }
    if (
      kennzeichen === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS &&
      !val["arh:Begruendung"]
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Begruendung is required when Kennzeichen_Verlegung is '3'.",
        path: ["arh:Begruendung"],
      });
    }
  });

const weitereInformationenV2 = z.object({
  ...antragInnerShape["arh:Weitere_Informationen"].shape,
  // v2.0: Besondere Anforderung - removed keys 10, 11, 17 (§6.11)
  // Valid keys: 01-09, 12-16; arh:Last_bis removed (was for Schwerlastbett 17)
  // v2.0: Koerpergroesse and Koerpergewicht are mandatory siblings of the choice
  "arh:Besondere_Anforderungen": z.object({
    "arh:Anforderungen": z
      .object({
        "arh:Anforderung": z
          .array(
            z.string().regex(/^(0[1-9]|1[2-6])$/, {
              message: "Must be a valid v2.0 Anforderung code (01-09, 12-16)",
            }),
          )
          .min(1)
          .max(14),
        "arh:Dialyseverfahren": z.string().max(150).optional(),
        "arh:Erlaeuterung": z.string().max(600).optional(),
        "arh:Grad_Dekubitus": z.enum(["1", "2", "3", "4"]).optional(),
        "arh:Pflegegrad_Angehoeriger": z
          .enum(["1", "2", "3", "4", "5"])
          .optional(),
      })
      .optional(),
    "arh:Keine_besonderen_Anforderungen": z.literal("1").optional(),
    "arh:Koerpergroesse": z.number().int().min(10).max(999),
    "arh:Koerpergewicht": z.number().int().min(1).max(999),
  }),
  "arh:Krankenhausarzt": z.object({
    ...antragInnerShape["arh:Weitere_Informationen"].shape[
      "arh:Krankenhausarzt"
    ].shape,
    "arh:Datum": AHBdateSchemaV2,
  }),
});

const funktionsdiagnosenV2 = z.object({
  "arh:Funktionsdiagnose": z
    .array(
      z.object({
        "arh:Diagnose": z.string().max(150),
        "arh:ICD_10_GM_Primaerdiagnose": ICDCodeSchema,
        "arh:Seitenlokalisation_Primaerdiagnose": z
          .enum(["l", "r", "b"])
          .optional(),
        "arh:ICD_10_GM_Sekundardiagnose": ICDCodeSchema.optional(),
        "arh:Seitenlokalisation_Sekundaerdiagnose": z
          .enum(["l", "r", "b"])
          .optional(),
        "arh:Lfd_Nr": z.enum(["1", "2", "3", "4", "5"]),
        "arh:Ursache": ICDReasonSchema,
      }),
    )
    .min(1)
    .max(5),
});

/** Schema for ARH request payload version 2.0. */
export const AHBRequestPayloadSchemaV2 = z.object({
  Nutzdaten: AHBRequestPayloadSchema.shape.Nutzdaten.extend({
    Header: z.object({
      ...headerShapeWithoutVersion,
      "gdb:Erstellungsdatum": AHBdateSchemaV2,
      "gdb:Logische_Version": z.literal(SCHEMA_VERSION.V2_0_0),
    }),
    Body: z.object({
      Anschlussrehabilitation: z.object({
        "aar:Antrag": z.object({
          ...antragOuterShape,
          "aar:Versicherter": versicherterV2,
          "aar:Antrag": z.object({
            ...antragInnerShapeV2,
            "arh:Angaben_des_Krankenhauses": angabenDesKrankenhausesV2,
            "arh:Krankenhausbehandlung": krankenhausbehandlungV2,
            "arh:Funktionsdiagnosen": funktionsdiagnosenV2,
            "arh:Behandlungen": behandlungenV2,
            "arh:Verlegung": verlegungV2,
            "arh:Weitere_Informationen": weitereInformationenV2,
            "arh:Zusammenfassende_Bewertung": zusammenfassendeBewertungV2,
          }),
        }),
      }),
    }),
  }),
});

export function getAHBRequestPayloadSchema(version: SchemaVersion) {
  return version === SCHEMA_VERSION.V2_0_0
    ? (AHBRequestPayloadSchemaV2 as unknown as typeof AHBRequestPayloadSchema)
    : AHBRequestPayloadSchema;
}
